import type { RefObject } from "react";
import { isNil } from "es-toolkit";
import type * as THREE from "three";
import type { MuscleId } from "@/entities/muscle/model/muscles";
import type { MuscleHighlightMap } from "@/entities/exercise/model/exercise-types";
import { MuscleLabel } from "@/entities/muscle/ui/muscle-label.ui";
import type { LimbPivots } from "@/entities/muscle/ui/limb-rigging.utils";
import {
  useAnatomySceneMeshes,
  useApplyMuscleHighlights,
} from "@/entities/muscle/ui/anatomy-scene.hooks";
import { AnatomyScene as AnatomySceneUtils } from "@/entities/muscle/ui/anatomy-scene.utils";

type AnatomySceneProps = Readonly<{
  clonedScene: THREE.Object3D;
  active: MuscleHighlightMap;
  activeHeads: ReadonlySet<string>;
  useClassifier: boolean;
  pivotsRef: RefObject<LimbPivots>;
  onActiveBox: ((box: THREE.Box3 | null) => void) | undefined;
  muscleLabels: Partial<Record<MuscleId, string>> | undefined;
}>;

type MuscleLabelsProps = Readonly<{
  labels: ReadonlyArray<readonly [MuscleId, string]>;
  muscleCenters: Partial<Record<MuscleId, [number, number, number]>>;
  active: MuscleHighlightMap;
}>;

const MuscleLabels = ({ labels, muscleCenters, active }: MuscleLabelsProps) => (
  <>
    {labels.map(([muscleId, text]) => {
      const center = muscleCenters[muscleId];
      if (isNil(center)) return null;

      return (
        <MuscleLabel
          key={`label-${muscleId}`}
          muscleId={muscleId}
          text={text}
          center={center}
          highlight={active[muscleId]}
        />
      );
    })}
  </>
);

export const AnatomyScene = ({
  clonedScene,
  active,
  activeHeads,
  useClassifier,
  pivotsRef,
  onActiveBox,
  muscleLabels,
}: AnatomySceneProps) => {
  const { muscleMeshesRef, genericMeshesRef, partMeshesRef, muscleCenters } = useAnatomySceneMeshes(
    {
      clonedScene,
      useClassifier,
      pivotsRef,
    },
  );

  useApplyMuscleHighlights({
    muscleMeshesRef,
    genericMeshesRef,
    partMeshesRef,
    clonedScene,
    active,
    activeHeads,
    onActiveBox,
  });

  const labels = AnatomySceneUtils.buildMuscleLabels(muscleLabels, muscleCenters);

  return (
    <>
      <primitive object={clonedScene} />
      <MuscleLabels labels={labels} muscleCenters={muscleCenters} active={active} />
    </>
  );
};
